import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button } from 'reactstrap'

import { exportShift, selectPlansStatus } from 'slices/plansSlice'
import { selectWorkspacesStatus } from 'slices/workspacesSlice'

import { CustomModal, ItemEdit, SingleDateRangePicker } from 'components/common'
import type { SuggestionItem } from 'components/common/types'

import useBusinessTime from 'hooks/useBusinessTime'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
  currentWorkspaceId?: number
}

const DATE_RANGE_MAX_DAYS = 31

const ShiftExportDialog = ({ isOpen, onSuccess, onCancel, currentWorkspaceId }: Props) => {
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<SuggestionItem[]>([])
  const [period, setPeriod] = useState<{ start: Date; end: Date }>()
  const [openRangeDatePicker, setOpenRangeDatePicker] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [modalErrorMessage, setModalErrorMessage] = useState<string | undefined>(undefined)

  const dispatch = useDispatch()
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { isRequesting, errorMessage } = useSelector(selectPlansStatus, shallowEqual)
  const { getWorkDate } = useBusinessTime()
  const workDate = useMemo(() => getWorkDate(dayjs().format('YYYY-MM-DD')), [getWorkDate])

  useEffect(() => {
    if (!isOpen || !currentWorkspaceId) {
      return
    }
    const target = partialWorkspaces.find(workspace => workspace.id === currentWorkspaceId)
    if (target) {
      setSelectedWorkspaces([{ id: currentWorkspaceId, value: target.name }])
    }
  }, [isOpen, currentWorkspaceId, partialWorkspaces])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }

    if (errorMessage === '') {
      setModalErrorMessage(undefined)
      onSuccess()
    } else {
      setModalErrorMessage('勤務表のエクスポートに失敗しました。')
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess])

  const handleApprove = useCallback(() => {
    if (!period || selectedWorkspaces.length === 0) {
      return
    }

    const targetWorkspaces = selectedWorkspaces.map(ws => (typeof ws.id === 'number' ? ws.id : Number(ws.id)))
    const request = {
      startDate: dayjs(period.start).format('YYYY-MM-DD'),
      endDate: dayjs(period.end).format('YYYY-MM-DD'),
      targetWorkspaces,
    }
    const fileName = `勤務表_${dayjs().format('YYYY-MM-DD')}.csv`

    dispatch(exportShift(request, fileName))
    setSubmitted(true)
  }, [dispatch, period, selectedWorkspaces])

  const handleCancelClick = useCallback(() => {
    setSelectedWorkspaces([])
    setPeriod(undefined)
    setModalErrorMessage(undefined)
    setSubmitted(false)
    onCancel()
  }, [onCancel])

  const disabled = useMemo(() => !period || selectedWorkspaces.length === 0, [period, selectedWorkspaces])

  const workspaceItems = useMemo(() => partialWorkspaces.map(w => ({ id: w.id, value: w.name })), [partialWorkspaces])

  return (
    <CustomModal
      isOpen={isOpen}
      title="勤務表エクスポート"
      onCancel={handleCancelClick}
      onHideNotification={() => setModalErrorMessage(undefined)}
      errorMessage={modalErrorMessage}
      approveLabel="CSVエクスポート"
      onApprove={handleApprove}
      approveDisabled={disabled}
      submitName="shift-export-submit"
    >
      <div className="pb-4">
        <div className="mb-4">
          <div className="my-2 fw-bold">ワークスペース選択</div>
          <ItemEdit
            items={workspaceItems}
            selectedItems={selectedWorkspaces}
            label="ワークスペースを追加"
            itemName="ワークスペース"
            onChange={setSelectedWorkspaces}
            showAddAllItemsButton
          />
        </div>

        <div className="mb-3">
          <div className="my-2 fw-bold">エクスポート期間設定</div>
          <div className="mb-2">最大31日分のエクスポートが可能です。</div>
          <div className="d-flex">
            <Button outline onClick={() => setOpenRangeDatePicker(true)}>
              {period
                ? `${dayjs(period.start).format('YYYY/MM/DD')} - ${dayjs(period.end).format('YYYY/MM/DD')}`
                : '開始日と終了日を決定'}
            </Button>
            <SingleDateRangePicker
              isOpen={openRangeDatePicker}
              from={period && period.start}
              to={period && period.end}
              maxRange={DATE_RANGE_MAX_DAYS}
              openToDate={dayjs(workDate).toDate()}
              onCancel={() => setOpenRangeDatePicker(false)}
              onChange={(start, end) => {
                setPeriod({ start, end })
                setOpenRangeDatePicker(false)
              }}
            />
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default ShiftExportDialog
